// Admin API service - always use relative URLs for correct server routing
const API_URL = '';

// Token storage
const TOKEN_KEY = 'jbf_admin_token';
const ADMIN_KEY = 'jbf_admin_user';

export const adminApi = {
  // Get stored token
  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  },

  // Get stored admin info
  getAdmin() {
    const admin = localStorage.getItem(ADMIN_KEY);
    return admin ? JSON.parse(admin) : null;
  },

  // Store auth data
  setAuth(token, admin) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(ADMIN_KEY, JSON.stringify(admin));
  },

  // Clear auth data
  clearAuth() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_KEY);
  },

  // Check if logged in
  isAuthenticated() {
    return !!this.getToken();
  },

  // Get auth headers
  getAuthHeaders() {
    const token = this.getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  },

  // Check if admin exists
  async checkAdminExists() {
    const response = await fetch(`${API_URL}/api/admin/check`);
    if (!response.ok) throw new Error('Failed to check admin status');
    return response.json();
  },

  // Register first admin
  async register(username, password, email) {
    const response = await fetch(`${API_URL}/api/admin/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, email })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }
    
    const data = await response.json();
    this.setAuth(data.access_token, data.admin);
    return data;
  },

  // Login
  async login(username, password) {
    const response = await fetch(`${API_URL}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }
    
    const data = await response.json();
    this.setAuth(data.access_token, data.admin);
    return data;
  },

  // Verify token
  async verifyToken() {
    const response = await fetch(`${API_URL}/api/admin/verify`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      this.clearAuth();
      return false;
    }
    
    return true;
  },

  // Logout
  logout() {
    this.clearAuth();
  },

  // Upload single image
  async uploadImage(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    let response;
    try {
      response = await fetch(`${API_URL}/api/admin/upload`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: formData
      });
    } catch (networkErr) {
      throw new Error('Network error - check your connection and try again');
    }
    
    if (!response.ok) {
      if (response.status === 413) {
        throw new Error('413 - Image too large for server');
      }
      try {
        const error = await response.json();
        throw new Error(error.detail || `Upload failed (${response.status})`);
      } catch (parseErr) {
        if (parseErr.message.includes('413')) throw parseErr;
        throw new Error(`Upload failed (${response.status})`);
      }
    }
    
    return response.json();
  },

  // Upload multiple images at once
  async uploadMultipleImages(files) {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    
    let response;
    try {
      response = await fetch(`${API_URL}/api/admin/upload-multiple`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: formData
      });
    } catch (networkErr) {
      throw new Error('Network error - check your connection and try again');
    }
    
    if (!response.ok) {
      if (response.status === 413) {
        throw new Error('413 - Images too large for server');
      }
      try {
        const error = await response.json();
        throw new Error(error.detail || `Upload failed (${response.status})`);
      } catch (parseErr) {
        if (parseErr.message.includes('413')) throw parseErr;
        throw new Error(`Upload failed (${response.status})`);
      }
    }
    
    return response.json();
  },

  // Create gallery item
  async createItem(item) {
    const response = await fetch(`${API_URL}/api/gallery`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify(item)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create item');
    }
    
    return response.json();
  },

  // Update gallery item
  async updateItem(id, updates) {
    const response = await fetch(`${API_URL}/api/gallery/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify(updates)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update item');
    }
    
    return response.json();
  },

  // Delete gallery item
  async deleteItem(id) {
    const response = await fetch(`${API_URL}/api/gallery/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete item');
    }
    
    return response.json();
  }
};

export default adminApi;
